import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useApolloClient, useLazyQuery } from '@apollo/client';
import Helmet from 'react-helmet';

import { useTranslation } from '@crehana/i18n';
import { notEmpty, matchMediaQuery } from '@crehana/utils';
import { useMe } from '@crehana/web/dist/ReactContexts/AuthContext';
import { useProductsContainer } from '@crehana/web/dist/ReactContexts/Products';

import {
  trackUserActivity,
  UserActivityEvent,
} from 'Jsx/Analytics/UserActivity';
import useIdleEffect from 'Jsx/Shared/Hooks/useIdleEffect';

import {
  formatCourseV2,
  checkIfisInShoppingCart,
  COURSES_PAGE_SIZE,
} from 'Jsx/Catalog/utils';
import COMMUNITY_RECOMMENDED_QUERY from 'Jsx/Catalog/graphql/recommededByCommunity.graphql';
import type {
  CatalogRedesignCoursesRecommendedToAnonymousUserByHanaQuery as RecommendedQuery,
  CatalogRedesignCoursesRecommendedToAnonymousUserByHanaQueryVariables as RecommendedQueryVariables,
} from 'Jsx/Catalog/graphql/types/CatalogRedesignCoursesRecommendedToAnonymousUserByHanaQuery';

import { CoursesFragment } from 'Jsx/Shared/CourseCardV3/graphql/types/CoursesFragment';

import CourseListSlider from './CourseListSlider';

const TABLET_COUNT = 3;
const MOBILE_COUNT = 1;
const getPreloadQuery = (total: number) =>
  `/api/v2/graph/?query=%7BcoursesRecommendedToAnonymousUserByHana(first%3A%20${total})%7BmodelName%20edges%7Bnode%7BoriginalId%20title%20promoImage%20urlToBuyNow%20isEnrollment%20courseType%20prices%7BrealPrice%20price%20currencySymbol%7Durl%20courseTag%7BtextLabel%20textColor%20backgroundColor%20name%20icon%7Denrollments%20categoryObj%7Bslug%20name%7DsubCategoryObj%7Bslug%20name%7Dlevel%7BoriginalId%20name%7DpreSale%20imagePlaceholder%20promoImageAnimated%20professor%7BfullName%7DenrollmentUrl%20urlToAddToCart%20languageTag%7BflagImageUrl%20text%7DlaunchDate%20score%7Baverage%20total%7DprincipalSoftwareObj%7BoriginalId%20originalImageUrl%20name%7Dsubcategory%7D%7D%7D%7D`;

function useRecommendedQuery() {
  const [preloadData, setPreloadData] = useState<RecommendedQuery | null>(null);
  const [preloadLoading, setPreloadLoading] = useState(true);
  const [preloadError, setPreloadError] = useState(false);
  const client = useApolloClient();
  const [getRecommended, { data, error }] = useLazyQuery<
    RecommendedQuery,
    RecommendedQueryVariables
  >(COMMUNITY_RECOMMENDED_QUERY, {
    variables: { pageSize: COURSES_PAGE_SIZE },
  });
  const {
    shoppingCartState: { courseIdsInCart },
  } = useProductsContainer();
  const { me, loading: loadingMe } = useMe();

  useEffect(() => {
    const { matches: isTablet } = matchMediaQuery('sm');
    const totalCourses = isTablet ? TABLET_COUNT : MOBILE_COUNT;

    fetch(getPreloadQuery(totalCourses))
      .then(res => res.json())
      .then(({ data: d }) => {
        setPreloadData(d);
        setPreloadLoading(false);
      })
      .catch(() => {
        setPreloadError(true);
        setPreloadLoading(false);
      });
  }, [client.cache]);

  useIdleEffect(() => getRecommended(), []);

  const courses = useMemo(() => {
    // Merge the preloaded data with the query data skipping the preloaded courses
    const allData = data?.coursesRecommendedToAnonymousUserByHana?.edges.length
      ? data.coursesRecommendedToAnonymousUserByHana.edges
      : preloadData?.coursesRecommendedToAnonymousUserByHana?.edges;

    return (
      allData
        ?.map(e => e?.node)
        .filter(notEmpty)
        .map(course => {
          // @ts-expect-error
          const formatted = formatCourseV2(course);

          return checkIfisInShoppingCart(courseIdsInCart)(formatted);
        }) || []
    );
  }, [data, preloadData, courseIdsInCart]);

  useEffect(() => {
    if (
      !loadingMe &&
      me &&
      data?.coursesRecommendedToAnonymousUserByHana &&
      data.coursesRecommendedToAnonymousUserByHana.edges.length
    ) {
      const recommendations = data.coursesRecommendedToAnonymousUserByHana.edges
        .map(edge => edge?.node?.originalId)
        .filter(notEmpty)
        .join(',');

      trackUserActivity({
        event: UserActivityEvent.RECOMMENDATION_VIEW,
        user: me
          ? {
              segment: me.segmentationKey!,
              username: me.username!,
            }
          : undefined,
        data: {
          item_type: 'course',
          recommendations,
          origin: 'catalog',
          model_used: `${data.coursesRecommendedToAnonymousUserByHana.modelName}`,
          total_items:
            data.coursesRecommendedToAnonymousUserByHana.edges.length,
        },
      });
    }
  }, [data, me, loadingMe]);

  const trackCourseClick = useCallback(
    (course: CoursesFragment) => {
      if (data?.coursesRecommendedToAnonymousUserByHana?.modelName) {
        const recommendations = courses
          .map(c => c.originalId)
          .filter(notEmpty)
          .join(',');

        const idx = courses.findIndex(c => c.originalId === course.originalId);

        if (idx >= 0) {
          trackUserActivity({
            event: UserActivityEvent.RECOMMENDATION_POSITION_CLICK,
            data: {
              item_id: course.originalId!,
              item_type: 'course',
              recommendations,
              position_selected: idx,
              origin: 'catalog',
              model_used:
                data?.coursesRecommendedToAnonymousUserByHana?.modelName,
            },
            nextUrl: course.url!,
          });
        }
      }
    },
    [courses, data],
  );

  // We use the preload loading to avoid showing the skeletons twice
  return {
    courses,
    error: error || preloadError,
    loading: preloadLoading,
    trackCourseClick,
  };
}

const CommunityRecommeded = () => {
  const { t } = useTranslation();
  const { courses, error, loading, trackCourseClick } = useRecommendedQuery();

  if (error) {
    return (
      <div className="py-24 mb-36">
        <span className="font-subtitle4 text-base-main">
          Ocurrió un error al intentar obtener los cursos recomendados, intente
          recargar la página.
        </span>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <link
          rel="preload"
          href={getPreloadQuery(TABLET_COUNT)}
          as="fetch"
          type="application/json"
          media="(min-width: 768px)"
          crossOrigin=""
        />
        <link
          rel="preload"
          href={getPreloadQuery(MOBILE_COUNT)}
          as="fetch"
          type="application/json"
          media="(max-width: 767px)"
          crossOrigin=""
        />
      </Helmet>
      <CourseListSlider
        courses={courses}
        title={t('COURSE_RECOMMENDATION')}
        hasMainTitle
        loading={loading}
        withPriority
        onClickCourse={trackCourseClick}
      />
    </>
  );
};

export default CommunityRecommeded;
